/**
 * Dashboard Plugin System - Translations
 * 
 * Handles plugin translation registration with automatic deep merge.
 * This ensures plugin translations don't overwrite existing core translations.
 * 
 * How it works:
 * 1. Plugin provides translation object (can be nested)
 * 2. System gets existing translations from i18n
 * 3. Deep merge combines both (plugin adds/overwrites only its keys)
 * 4. Result preserves all core translations + adds plugin translations
 * 
 * Example:
 * 
 * Core has:
 * {
 *   INBOX_MGMT: {
 *     ADD: {
 *       WEBSITE_CHANNEL: { ... },
 *       WHATSAPP: { ... }
 *     }
 *   }
 * }
 * 
 * Plugin registers:
 * {
 *   INBOX_MGMT: {
 *     ADD: {
 *       QUEPASA: { ... }
 *     }
 *   }
 * }
 * 
 * Result (after deep merge):
 * {
 *   INBOX_MGMT: {
 *     ADD: {
 *       WEBSITE_CHANNEL: { ... },  ← Core preserved
 *       WHATSAPP: { ... },          ← Core preserved
 *       QUEPASA: { ... }            ← Plugin added
 *     }
 *   }
 * }
 * 
 * Without deep merge (shallow merge), INBOX_MGMT.ADD would be replaced entirely,
 * losing WEBSITE_CHANNEL and WHATSAPP.
 */

import type { I18n } from 'vue-i18n';

/**
 * Deep merge two objects recursively
 * 
 * - Objects: Merged recursively at all levels
 * - Arrays: Overwritten (not merged)
 * - Primitives: Overwritten
 * 
 * @param target - Base object (usually existing translations)
 * @param source - New object to merge (usually plugin translations)
 * @returns Merged object with all nested properties combined
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  source: Record<string, any>
): T {
  const result = { ...target };

  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      // Recursively merge nested objects
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      // Overwrite primitives and arrays
      result[key] = source[key];
    }
  }

  return result;
}

/**
 * Register plugin translations with automatic deep merge
 * 
 * This is the recommended way for plugins to add translations.
 * It safely merges plugin translations into existing translations
 * without losing any core translation keys.
 * 
 * @param i18n - Vue I18n instance from the app
 * @param translations - Plugin translation object (nested structure)
 * @param locale - Locale to register (default: 'en')
 * 
 * @example
 * registerPluginTranslations(i18n, {
 *   INBOX_MGMT: {
 *     ADD: {
 *       QUEPASA: {
 *         TITLE: 'QuePasa',
 *         DESC: 'QuePasa WhatsApp provider'
 *       }
 *     }
 *   }
 * });
 */
export function registerPluginTranslations(
  i18n: I18n,
  translations: Record<string, any>,
  locale: string = 'en'
): void {
  if (!i18n || !i18n.global) {
    console.error('[PLUGIN-TRANSLATIONS] i18n not available');
    return;
  }

  try {
    // Get existing translations for this locale
    const existingMessages = i18n.global.getLocaleMessage(
      locale
    ) as Record<string, any>;

    // Deep merge plugin translations into existing messages
    const mergedMessages = deepMerge(existingMessages, translations);

    // Set the updated messages back
    i18n.global.setLocaleMessage(locale, mergedMessages);
  } catch (error) {
    console.error('[PLUGIN] Translation registration failed:', error);
  }
}

/**
 * Register translations for multiple locales at once
 * 
 * @param i18n - Vue I18n instance
 * @param translationsByLocale - Object with locale keys and translation objects
 * 
 * @example
 * registerMultiLocaleTranslations(i18n, {
 *   en: { INBOX_MGMT: { ... } },
 *   pt: { INBOX_MGMT: { ... } },
 *   es: { INBOX_MGMT: { ... } }
 * });
 */
export function registerMultiLocaleTranslations(
  i18n: I18n,
  translationsByLocale: Record<string, Record<string, any>>
): void {
  Object.entries(translationsByLocale).forEach(([locale, translations]) => {
    registerPluginTranslations(i18n, translations, locale);
  });
}
